import { useState, useCallback, useEffect, useRef } from 'react';
import { Translate, translate } from 'react-i18nify';
import { connect } from 'react-redux';
import { Prompt, Redirect } from 'react-router-dom';
import {
  updateMemberRequest,
  updateMemberSuccess,
  updateMemberError,
  getMember,
  showNotification
} from '../../../actions';
import { APPLICATION_BASE_PATH } from '../../../constants';
import EditableText from '../../EditableText';
import LoaderButton from '../../LoaderButton';
import LoadingIcon from '../../LoadingIcon';
import Card from '../../Card';
import api from '../../../api/apiClient';
import Button from '../../Button';

const preventNavigation = (e) => {
  e.preventDefault();
  e.returnValue = '';
};

const EditMemberDetailsView = ({
  authToken,
  members,
  match,
  updateMemberRequest,
  updateMemberSuccess,
  updateMemberError,
  getMember,
  showNotification
}) => {
  const [memberUpdates, setMemberUpdates] = useState({});
  const [renderRedirect, setRenderRedirect] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isUpdatingFromSpar, setIsUpdatingFromSpar] = useState(false);

  const hasChangesRef = useRef(hasChanges);
  hasChangesRef.current = hasChanges;

  useEffect(() => {
    return () => {
      window.removeEventListener('beforeunload', preventNavigation);
    };
  }, []);

  const getLatestDetailsFromSpar = () => {
    setIsUpdatingFromSpar(true);

    api
      .getMemberSparData(authToken, match.params.memberId)
      .then(() => {
        getMember(authToken, match.params.memberId, undefined, () => {
          showNotification(translate('notification.update_from_spar.success'), 'success');
        });
      })
      .catch(() => {
        showNotification(translate('notification.update_from_spar.error'), 'error');
      })
      .finally(() => setIsUpdatingFromSpar(false));
  };

  const saveChanges = (e) => {
    e.preventDefault();

    updateMemberRequest();

    api
      .updateMember(authToken, match.params.memberId, memberUpdates)
      .then((response) => {
        updateMemberSuccess(response);
        setHasChanges(false);
        setRenderRedirect(true);
        window.removeEventListener('beforeunload', preventNavigation);
      })
      .catch((error) => {
        console.log(error);
        updateMemberError(error);
      });
  };

  const updateProperty = (event, property) => {
    let updatedValue = event.target.value;

    if (property === 'responsibleDoctor') {
      const selectedDoctor = members.assignableDoctors[parseInt(event.target.value, 10)];

      updatedValue = {
        guid: selectedDoctor.guid,
        givenName: selectedDoctor.givenName,
        familyName: selectedDoctor.familyName,
        role: 'caregiver'
      };
    }

    setMemberUpdates((prev) => ({
      ...prev,
      [property]: updatedValue
    }));
    setHasChanges(true);

    window.addEventListener('beforeunload', preventNavigation);
  };

  const { currentMember, memberUpdateError, updatingMember } = members;

  if (!currentMember.personalNumber || renderRedirect) {
    return <Redirect to={`/${APPLICATION_BASE_PATH}/member/${match.params.memberId}`} />;
  }

  return (
    <>
      <Prompt
        message={(location) => {
          if (hasChangesRef.current && location.pathname.indexOf('edit') === -1) {
            return translate('member_details.unsaved_changes_prompt');
          }

          return true;
        }}
      />
      <div className="edit-profile-container">
        <Card
          titleI18nKey="member_details.edit_profile"
          actionButtons={
            <div className="fs-15 flex vertical-align gap-1">
              {isUpdatingFromSpar && <LoadingIcon type="spinner-secondary" size="small" />}
              <Button buttonType="text" onClick={getLatestDetailsFromSpar}>
                Uppdatera från folkbokföringen
              </Button>
            </div>
          }
        >
          <div className="columns">
            <div className="column">
              <h4>
                <Translate value="member_details.given_name" />
              </h4>
              <EditableText text={currentMember.givenName} />
            </div>
            <div className="column">
              <h4>
                <Translate value="member_details.family_name" />
              </h4>
              <EditableText text={currentMember.familyName} />
            </div>
          </div>
          <div className="columns">
            <div className="column">
              <h4>
                <Translate value="member_details.street_address" />
              </h4>
              <EditableText text={currentMember.streetaddress} />
            </div>
          </div>
          <div className="columns">
            <div className="column">
              <h4>
                <Translate value="member_details.postal_code" />
              </h4>
              <EditableText text={currentMember.postalCode} />
            </div>
            <div className="column">
              <h4>
                <Translate value="member_details.city" />
              </h4>
              <EditableText text={currentMember.city} />
            </div>
          </div>

          <hr />

          <form onSubmit={saveChanges}>
            <div className="columns">
              <div className="column">
                <h4>
                  <Translate value="member_details.email" />
                </h4>
                <EditableText
                  text={currentMember.email}
                  type="email"
                  pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                  maxLength="254"
                  isEditing={true}
                  onChange={(e) => updateProperty(e, 'email')}
                />
              </div>
            </div>
            <div className="columns">
              <div className="column">
                <h4>
                  <Translate value="member_details.phone_number" />
                </h4>
                <EditableText
                  text={currentMember.phoneNumber}
                  type="tel"
                  pattern="[0-9-\+\s]+"
                  maxLength="20"
                  isEditing={true}
                  onChange={(e) => updateProperty(e, 'phoneNumber')}
                />
              </div>
            </div>
            <div className="columns update-actions">
              <div className="column is-8">
                {memberUpdateError ? (
                  <span className="error-message-block">
                    <Translate value="member_details.update_profile_error" />
                  </span>
                ) : null}
              </div>
              <div className="column is-4">
                <LoaderButton buttonType="primary" type="submit" isLoading={updatingMember}>
                  <Translate value="member_details.save_details" />
                </LoaderButton>
              </div>
            </div>
          </form>
        </Card>
      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    authToken: state.auth.token.jwt,
    members: state.members
  };
};

const mapActionsToProps = {
  updateMemberRequest,
  updateMemberSuccess,
  updateMemberError,
  getMember,
  showNotification
};

export default connect(mapStateToProps, mapActionsToProps)(EditMemberDetailsView);
